import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
    Input,
    Form,
    Space,
    Popover,
    Flex,
    Row,
    Col,
    message,
    Empty,
    Spin,
    Typography,
    Modal,
    Card,
    Avatar,
    Select,
} from 'antd';
import _ from 'lodash';
import { PageHeader } from '../../components/page-headers/page-headers';
import { BasicFormWrapper, Main } from '../styled';
import { getProfileUrl } from '../../utility/utility';
import { useAuth } from '../../context/auth.context';
import useApiFetch from '../../hooks/useApiFetch';
import FeatherIcon from 'feather-icons-react';
import qs from 'qs';
import { Button } from '../../components/buttons/buttons';
import moment from 'moment-timezone';
import { CommunicationPage } from '../communication-style';

function ResponseTemplatesList({ match }) {
    const { apiClient } = useAuth();
    const { to } = useParams();
    const [editMailForm] = Form.useForm();
    const history = useHistory();
    const [selectedTemplate, setSelectedTemplate] = useState({
        templateId: null,
        mailSenderId: null,
        updating: false,
        name: '',
    });
    const [syncing, setSyncing] = useState(false);
    const {
        data: templates,
        // error: templatesError,
        loading: templatesLoading,
        refetch: refetchTemplates,
    } = useApiFetch(`/api/notification-templates?${qs.stringify({ to })}`, (data) => data.data, true);
    const {
        data: mailSenders,
        loading: mailSendersLoading,
        refetch: refetchMailSenders,
    } = useApiFetch(`/api/mail-senders`, (data) => data.data, true);

    useEffect(() => {
        refetchTemplates(`/api/notification-templates?${qs.stringify({ to: match.params.to })}`);
    }, [match]);

    useEffect(() => {
        if (selectedTemplate?.templateId && !mailSenders) {
            refetchMailSenders();
        }
    }, [selectedTemplate]);

    const handleEditMailSettings = async (values) => {
        try {
            setSelectedTemplate({
                ...selectedTemplate,
                updating: true,
            });
            const res = await apiClient.put(
                `/api/notification-templates?templateId=${selectedTemplate.templateId}`,
                values,
            );
            message.success(res?.data?.message || 'Mail Settings Updated Successfully');
            refetchTemplates();
        } catch (error) {
            console.error(error);
            message.error(
                error.response?.data?.message ||
                'An error occurred while updating mail settings. Please try again later.',
            );
        } finally {
            editMailForm.resetFields();
            setSelectedTemplate({
                templateId: null,
                mailSenderId: null,
                updating: false,
                name: '',
            });
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        try {
            const res = await apiClient.post('/api/notification-templates/sync');
            const { added = [], updated = [] } = res?.data?.data || {};
            // Awaited on purpose: the button must stay disabled until the new list is on screen.
            await refetchTemplates();
            message.success(
                added.length || updated.length
                    ? `${added.length} added, ${updated.length} updated`
                    : 'Already up to date',
            );
        } catch (error) {
            message.error(error.response?.data?.message || 'Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    const handleReset = (template) => {
        Modal.confirm({
            title: 'Reset to default?',
            content: `"${template.name}" goes back to the CommKraft default design and subject. Your edits to this template are lost. Its mail sender setting is kept.`,
            okText: 'Reset',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    const res = await apiClient.post(
                        `/api/notification-templates/reset?templateId=${template.uuid}`,
                    );
                    await refetchTemplates();
                    message.success(res?.data?.message || 'Template reset to default');
                } catch (error) {
                    message.error(error.response?.data?.message || 'Reset failed');
                }
            },
        });
    };

    return (
        <CommunicationPage>
            <PageHeader
                routes={[
                    {
                        path: '/admin/overview',
                        icon: 'home',
                    },
                    {
                        breadcrumbName: 'Notification Templates',
                        path: '#',
                    },
                    {
                        breadcrumbName: `To ${_.startCase(to)}`,
                        path: window.location.pathname,
                    },
                ]}
                title={(to || '') + ' Notification Templates'}
                buttons={[
                    <Button
                        key="sync"
                        size="small"
                        type="primary"
                        loading={syncing}
                        disabled={syncing || templatesLoading}
                        onClick={handleSync}
                    >
                        <FeatherIcon icon="refresh-cw" size={14} />
                        Sync Templates
                    </Button>,
                ]}
            />
            <Main className="communication-main notification-templates-main">
                <Spin spinning={templatesLoading}>
                    {templates?.length > 0 ? (
                        <Row gutter={[30, 30]}>
                            {templates?.map((template) => (
                                <Col xxl={6} lg={8} xs={24} key={template.slug || template.id}>
                                    <Card
                                        bordered
                                        cover={
                                            template.htmlContent ? (
                                                // Live thumbnail of the real template. Renders the same htmlContent
                                                // the list response already carries, so it can never go stale the
                                                // way the old S3 screenshot did. sandbox="" = no scripts, no forms,
                                                // no same-origin, which matters because this HTML is org-authored.
                                                <div
                                                    style={{
                                                        height: '15rem',
                                                        overflow: 'hidden',
                                                        position: 'relative',
                                                        background: '#fff',
                                                        borderBottom: '1px solid #f0f0f0',
                                                    }}
                                                >
                                                    <iframe
                                                        title={template.name}
                                                        srcDoc={template.htmlContent}
                                                        sandbox=""
                                                        scrolling="no"
                                                        loading="lazy"
                                                        style={{
                                                            // 200% at scale(0.5) = exactly the card width, whatever
                                                            // that is, so no white gutter on the right.
                                                            width: '200%',
                                                            height: '480px',
                                                            border: 0,
                                                            transformOrigin: 'top left',
                                                            transform: 'scale(0.5)',
                                                            pointerEvents: 'none',
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div
                                                    style={{
                                                        height: '15rem',
                                                        padding: '1rem',
                                                    }}
                                                >
                                                    <FeatherIcon
                                                        icon={'image'}
                                                        size={50}
                                                        style={{
                                                            color: '#f0f0f0',
                                                            width: '100%',
                                                            height: '100%',
                                                        }}
                                                    />
                                                </div>
                                            )
                                        }
                                        actions={[
                                            <Button
                                                type={'white'}
                                                size={'small'}
                                                icon={<FeatherIcon icon="edit" size={14} />}
                                                href={`/admin/notification-templates/edit/${template.uuid}`}
                                            >
                                                Edit
                                            </Button>,
                                            <Button
                                                onClick={() => {
                                                    editMailForm.resetFields();
                                                    setSelectedTemplate({
                                                        updating: false,
                                                        templateId: template.uuid,
                                                        name: template.name,
                                                        mailSenderId: template.mailSenderId,
                                                    });
                                                }}
                                                type={'white'}
                                                size={'small'}
                                                icon={<FeatherIcon icon="settings" size={14} />}
                                            >
                                                Mail sender
                                            </Button>,
                                            <Button
                                                onClick={() => handleReset(template)}
                                                type={'white'}
                                                size={'small'}
                                                icon={<FeatherIcon icon="rotate-ccw" size={14} />}
                                            >
                                                Reset
                                            </Button>,
                                        ]}
                                    >
                                        <Card.Meta
                                            title={template.name}
                                            description={
                                                template.updatedAt &&
                                                template.lastModifiedBy && (
                                                    <Space direction="horizontal">
                                                        <Avatar
                                                            src={getProfileUrl({
                                                                currentUser:
                                                                    template.lastModifiedBy || template.createdBy,
                                                            })}
                                                        />
                                                        <Typography.Text type="secondary" ellipsis>
                                                            {template?.lastModifiedBy ? 'Updated ' : 'Created '}
                                                            {moment(template.updatedAt).fromNow()}
                                                            {' by '}
                                                            {template.lastModifiedBy?.firstName || 'Unknown'}
                                                        </Typography.Text>
                                                    </Space>
                                                )
                                            }
                                        />
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        <Empty description="No Templates" />
                    )}
                </Spin>

                <Modal
                    footer={null}
                    open={selectedTemplate.templateId !== null}
                    onCancel={() => {
                        setSelectedTemplate({
                            templateId: null,
                            saving: false,
                            name: '',
                        });
                    }}
                    title={`Edit Mail Sender for '${selectedTemplate.name}'`}
                >
                    <BasicFormWrapper>
                        <Form
                            onFinish={handleEditMailSettings}
                            form={editMailForm}
                            initialValues={{
                                mailSenderId: selectedTemplate.mailSenderId,
                            }}
                        >
                            <Form.Item
                                rules={[{ required: true, message: 'Please select a mail sender' }]}
                                label="Mail Sender"
                                name="mailSenderId"
                            >
                                <Select
                                    showSearch
                                    loading={mailSendersLoading}
                                    options={mailSenders?.map((sender) => ({
                                        label: `${sender.fromName} <${sender.fromEmail}>`,
                                        value: sender.id,
                                    }))}
                                />
                            </Form.Item>
                            <div className="sDash_form-action">
                                <Button
                                    type={'light'}
                                    onClick={() => {
                                        editMailForm.resetFields();
                                        setSelectedTemplate({
                                            templateId: null,
                                            updating: false,
                                            name: '',
                                            mailSenderId: null,
                                        });
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    size={'default'}
                                    htmlType="submit"
                                    loading={selectedTemplate.updating}
                                >
                                    Save
                                </Button>
                            </div>
                        </Form>
                    </BasicFormWrapper>
                </Modal>
            </Main>
        </CommunicationPage>
    );
}

export default ResponseTemplatesList;
